import { Instance, types } from "mobx-state-tree";
import { createContext, useContext } from "react";
import { Lang } from "../localization/lang";
import { withReset } from "./_extension/with-reset";
import { withSetPropAction } from "./_extension/with-set-props";
import { setRootStoreAccessor } from "./_root-store-accessor";
import { Authentication } from "./stores/authentication";
import { CatalogueStore } from "./stores/catalogue-store";
import { ValidationErrorStore } from "./stores/common/validation-error-store";
import { CustomerManagement } from "./stores/customer-management";
import { DeleteConfirmationStore } from "./stores/delete-confirmation";
import { DeliveryNoteManagement } from "./stores/delivery-note-store";
import { DualLangInspectionNoteManagement } from "./stores/dual-lang-inspection-note-store";
import { EmployeeManagement } from "./stores/employee-management";
import { InspectionNoteManagement } from "./stores/inspection-note-management";
import { MaterialInventoryManagement } from "./stores/material-inventory-store";
import { MaterialStore } from "./stores/material-store";
import { OrderManagement } from "./stores/order-management-store";
import { PackingNoteManagement } from "./stores/packing-note-store";
import { PaperInventoryManagement } from "./stores/paper-inventory-store";
import { PaperMaterialStore } from "./stores/paper-material-store";
import { ProductInventoryManagement } from "./stores/product-inventory-store";
import { ProductManagement } from "./stores/product-management";
import { QuotationManagement } from "./stores/quotation-store";
import { RolePermissionManagement } from "./stores/role-permission-management";
import { StellaInspectionNoteManagement } from "./stores/stella-inspection-note-store";
import { UserManagement } from "./stores/user-management";
import { WorkOrderPoManagement } from "./stores/work-order-po-store";
import { WorkOrderManagement } from "./stores/work-order-store";

const RootStore = types
  .model({
    appFreeze: types.optional(types.boolean, false),
    lang: types.optional(
      types.enumeration<Lang>("Lang", Object.values(Lang)),
      Lang.viVN,
    ),
    mainNavigationMinimize: false,
    authentication: types.optional(Authentication, {}),
    userManagement: types.optional(UserManagement, {}),
    customerManagement: types.optional(CustomerManagement, {}),
    orderManagement: types.optional(OrderManagement, {}),
    productManagement: types.optional(ProductManagement, {}),
    rolePermissionManagement: types.optional(RolePermissionManagement, {}),
    catalogueStore: types.optional(CatalogueStore, {}),
    deleteConfirmationStore: types.optional(DeleteConfirmationStore, {}),
    validationErrorStore: types.optional(ValidationErrorStore, {}),
    paperMaterialStore: types.optional(PaperMaterialStore, {}),
    materialStore: types.optional(MaterialStore, {}),
    paperInventoryManagement: types.optional(PaperInventoryManagement, {}),
    materialInventoryManagement: types.optional(
      MaterialInventoryManagement,
      {},
    ),
    productInventoryManagement: types.optional(ProductInventoryManagement, {}),
    quotationManagement: types.optional(QuotationManagement, {}),
    deliveryNoteManagement: types.optional(DeliveryNoteManagement, {}),
    packingNoteManagement: types.optional(PackingNoteManagement, {}),
    workOrderManagement: types.optional(WorkOrderManagement, {}),
    workOrderPoManagement: types.optional(WorkOrderPoManagement, {}),
    stellaInspectionNoteManagement: types.optional(
      StellaInspectionNoteManagement,
      {},
    ),
    dualLangInspectionNoteManagement: types.optional(
      DualLangInspectionNoteManagement,
      {},
    ),
    inspectionNoteManagement: types.optional(InspectionNoteManagement, {}),
    employeeManagement: types.optional(EmployeeManagement, {}),
    inProgress: false,
  })
  .actions(withSetPropAction)
  .actions(withReset);

export let initialState = RootStore.create({});
setRootStoreAccessor({
  setValidationErrorVisible: (isVisible: boolean) => {
    initialState.validationErrorStore.setProp("show", isVisible);
  },
  setValidationErrorMessage: (message: string) => {
    initialState.validationErrorStore.setProp("errorMessage", message);
  },
});
// if (isDebug()) {
//   addMiddleware(initialState, mstLog());
// }

export type RootStoreInstance = Instance<typeof RootStore>;

const RootStoreContext = createContext<null | RootStoreInstance>(null);

//Hook for react function component
export function useStores() {
  let rootStore = useContext(RootStoreContext);

  if (rootStore === null) {
    rootStore = initialState;
  }

  return rootStore;
}

export interface IRootStore extends Instance<typeof RootStore> {}
